import { useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { topBannerHeightAtom } from '../atoms/topBanner';

export const useBannerHeightSync = () => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const setHeight = useSetAtom(topBannerHeightAtom);
  const [observer, setObserver] = useState<ResizeObserver | null>(null);

  useEffect(() => {
    const updateHeight = () => {
      const el = bannerRef.current;
      if (!el) {
        return;
      }

      let height = el.offsetHeight;
      if (height === 0 && el.firstElementChild) {
        height = (el.firstElementChild as HTMLElement).offsetHeight;
      }

      setHeight(height);
    };

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (bannerRef.current) {
      resizeObserver.observe(bannerRef.current);
    }

    if (bannerRef.current?.firstElementChild) {
      resizeObserver.observe(bannerRef.current.firstElementChild);
    }

    window.addEventListener('resize', updateHeight);

    setObserver(resizeObserver);

    updateHeight();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [setHeight]);

  useEffect(() => {
    if (!observer || !bannerRef.current) {
      return;
    }

    const mutationObserver = new MutationObserver(() => {
      const el = bannerRef.current;
      if (!el) {
        return;
      }

      observer.disconnect();
      observer.observe(el);

      if (el.firstElementChild) {
        observer.observe(el.firstElementChild);
      }

      // Оновлюємо висоту
      let height = el.offsetHeight;
      if (height === 0 && el.firstElementChild) {
        height = (el.firstElementChild as HTMLElement).offsetHeight;
      }
      setHeight(height);
    });

    mutationObserver.observe(bannerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => mutationObserver.disconnect();
  }, [observer, setHeight]);

  return bannerRef;
};
